/**
 * PlanRenderer — renders a `plan` artifact (the agent's research plan emitted
 * at plan-ready time).
 *
 * Wraps `<ArtifactFrame>` with a doc-shaped body that surfaces the four
 * sections the planner is contracted to produce:
 *
 *   - "How I understand the question" → `payload.understanding` (markdown)
 *   - "Expected result"               → `payload.expected_result` (markdown)
 *   - "Sources I'll consult"          → bulleted list of `sources_to_consult`
 *   - "Todos"                         → list of `todos` with status badges
 *
 * The `deliverable_format` field, when present, is rendered as a small chip
 * next to the title. Empty fields are silently skipped — the contract says
 * the renderer must work with partial payloads, since markdown parsing on
 * the backend is best-effort.
 *
 * Approval controls (Phase 1, workspace-focus refactor):
 * When `planProposed && !planApproved` (read from the AI Explorer store
 * for the active session), the renderer shows three inline controls in
 * the card body, beneath the plan content:
 *
 *   - Approve & Run         → onAction("plan.approve", artifact)
 *   - Approve in background → onAction("plan.approve_background", artifact)
 *   - Suggest changes       → onAction("plan.reject", artifact, { feedback })
 *   - Cancel plan           → onAction("plan.cancel", artifact)
 *
 * Once approved (or after legacy mode dismisses planProposed), the controls
 * disappear and the renderer reverts to its read-only summary. The
 * page-level dispatcher (`ai-explorer-page.tsx`) routes the `plan.*` action
 * ids to `sendPlanDecision` (approve/revise/cancel) and `handleApproveInBackground`.
 * The legacy ``plan.review`` action and the ``<PlanPreviewModal>`` portal
 * were removed in the 2026-05-12 UX collapse — surgical step editing proved
 * error-prone (broke the markdown parser, bypassed planner context); users
 * now describe what should change via the inline Suggest changes textarea
 * and the planner re-issues a v2 plan.
 *
 * Default actions: when the artifact ships no actions, we add a `view_trace`
 * affordance if a mission_id is present (so the user can scope the activity
 * sidebar to this plan's mission). The legacy in-chat-rail "Plan ready" bar
 * is hidden in workspace mode — these controls replace it.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 *       .workspace-spec/PLAN_WORKSPACE_FOCUS.md (Phase 1)
 */
import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Artifact, ArtifactAction, PlanPayload, PlanTodo } from "@/api/models"

import { CheckCircle2, Circle, Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"

import { ArtifactFrame } from "./ArtifactFrame"
import { WorkspaceMarkdownRenderer } from "./WorkspaceMarkdownRenderer"

/** Optional 3rd arg on a plan action — currently only `plan.reject` carries
 *  user feedback. Kept open so future plan.* actions can extend without
 *  breaking the existing onAction wire shape. */
export interface PlanActionExtra {
  feedback?: string
}

export interface PlanRendererProps {
  artifact: Artifact & { payload: PlanPayload }
  versionChain?: Artifact[]
  /**
   * Receives `(actionId, artifact)` for normal artifact actions, plus an
   * optional 3rd arg with `{ feedback }` for `plan.reject`. Existing
   * dispatchers that only declare `(id, artifact)` still type-check — TS
   * allows ignoring trailing optional params.
   */
  onAction?: (actionId: string, artifact: Artifact, extra?: PlanActionExtra) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
  citationSection?: string
  /**
   * Phase 1 (workspace-focus): when set, the renderer subscribes to the
   * matching AI Explorer namespaced store and surfaces the four approval
   * controls when `planProposed && !planApproved`. Omit this prop in legacy
   * fallback (or in unit-test rendering) — the approval row stays hidden
   * and the renderer behaves exactly as before.
   */
  sessionType?: string
}

const STATUS_BADGE_STYLES: Record<PlanTodo["status"], string> = {
  // Subtle pill colors — match the rest of the workspace tints (low-saturation
  // backgrounds with stronger foreground text).
  pending: "bg-muted text-muted-foreground border-border",
  in_progress: "bg-warning/30 text-warning-ink border-warning-ink ",
  completed: STATUS_TONES.success.soft,
}

const STATUS_LABEL: Record<PlanTodo["status"], { key: string; fallback: string }> = {
  pending: { key: "deepExplorer.plan_workspace.status_pending", fallback: "Pending" },
  in_progress: { key: "deepExplorer.plan_workspace.status_in_progress", fallback: "In progress" },
  completed: { key: "deepExplorer.plan_workspace.status_done", fallback: "Done" },
}

function StatusIcon({ status }: { status: PlanTodo["status"] }) {
  if (status === "completed") {
    return <CheckCircle2 aria-hidden className="text-success size-4 shrink-0" />
  }
  if (status === "in_progress") {
    return <Loader2 aria-hidden className="text-warning-ink size-4 shrink-0 animate-spin" />
  }
  return <Circle aria-hidden className="text-muted-foreground size-4 shrink-0" />
}

interface PlanApprovalControlsProps {
  artifact: Artifact
  sessionType: string
  onAction?: (actionId: string, artifact: Artifact, extra?: PlanActionExtra) => void
}

/**
 * Approval row — Approve & Run / Approve in background / Review / Reject.
 *
 * Self-gated: subscribes to the namespaced AI Explorer store and only renders
 * when `planProposed && !planApproved` for the current session. When neither
 * flag is set (e.g. legacy fallback or post-approval), the entire row is
 * absent from the DOM.
 *
 * Reject opens an inline feedback textarea. Submit dispatches
 * `plan.reject` with `{ feedback }` in the 3rd arg of `onAction`. The
 * page-level dispatcher routes that to `sendPlanDecision("revise", { feedback })`
 * — the backend's plan-decision contract uses "revise" as the
 * reject-with-feedback variant.
 */
function PlanApprovalControls({ artifact, sessionType, onAction }: PlanApprovalControlsProps) {
  const { t } = useTranslation()
  const explorerStore = useAIExplorerStore(sessionType)
  const planProposed = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).planProposed
  )
  const planApproved = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).planApproved
  )

  const [rejecting, setRejecting] = useState(false)
  const [feedback, setFeedback] = useState("")

  if (!planProposed || planApproved) return null

  const trimmed = feedback.trim()

  return (
    <div
      className="border-border/60 mt-4 flex flex-col gap-2 border-t pt-3"
      data-testid="plan-approval-controls"
    >
      {!rejecting ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Cancel ends the run at the plan gate, the same "cancel" decision
              the side panel's Reject sends. Without it the workspace offered
              no way out but waiting for the 300 s plan timeout (#3323 E8). */}
          <Button
            className="text-muted-foreground mr-auto"
            data-testid="plan-action-cancel"
            size="sm"
            variant="ghost"
            onClick={() => onAction?.("plan.cancel", artifact)}
          >
            {t("deepExplorer.plan_workspace.cancel_plan", {
              defaultValue: "Cancel plan",
            })}
          </Button>
          {/* Single revision path (2026-05-12): "Suggest changes" opens an
              inline free-text textarea — dispatched as `plan.reject` with a
              feedback string. The older "Edit steps" markdown-edit affordance
              was removed; telling the planner what to change reliably produces
              a v2 plan, whereas hand-editing the bullet list bypassed planner
              context and broke the parser when users tweaked structure. */}
          <Button
            data-testid="plan-action-reject"
            size="sm"
            variant="ghost"
            onClick={() => {
              setRejecting(true)
              setFeedback("")
            }}
          >
            {t("deepExplorer.plan_workspace.suggest_changes", {
              defaultValue: "Suggest changes",
            })}
          </Button>
          <Button
            data-testid="plan-action-approve-background"
            size="sm"
            variant="outline"
            onClick={() => onAction?.("plan.approve_background", artifact)}
          >
            {t("deepExplorer.plan_workspace.approve_background", {
              defaultValue: "Approve in background",
            })}
          </Button>
          <Button
            data-testid="plan-action-approve"
            size="sm"
            variant="default"
            onClick={() => onAction?.("plan.approve", artifact)}
          >
            {t("deepExplorer.plan_workspace.approve_and_run", {
              defaultValue: "Approve & Run",
            })}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="plan-reject-feedback">
          <label
            className="text-muted-foreground text-xs font-medium"
            htmlFor={`plan-reject-feedback-${artifact.id}`}
          >
            {t("deepExplorer.plan_workspace.describe_changes_label", {
              defaultValue:
                "Describe what you'd like changed. The agent will revise the plan and ask you to approve again.",
            })}
          </label>
          <Textarea
            className="min-h-[72px] resize-y px-2 py-1.5 text-sm"
            data-testid="plan-reject-feedback-textarea"
            id={`plan-reject-feedback-${artifact.id}`}
            value={feedback}
            placeholder={t("deepExplorer.plan_workspace.describe_changes_placeholder", {
              defaultValue: "e.g., make it shorter, focus on Q4 only, skip the comparison…",
            })}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              data-testid="plan-reject-cancel"
              size="sm"
              variant="ghost"
              onClick={() => {
                setRejecting(false)
                setFeedback("")
              }}
            >
              {t("deepExplorer.plan_workspace.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              data-testid="plan-reject-submit"
              disabled={!trimmed}
              size="sm"
              variant="default"
              onClick={() => {
                onAction?.("plan.reject", artifact, { feedback: trimmed })
                setRejecting(false)
                setFeedback("")
              }}
            >
              {t("deepExplorer.plan_workspace.send_feedback", {
                defaultValue: "Send feedback",
              })}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function PlanSection({
  heading,
  children,
  testId,
}: {
  heading: string
  children: React.ReactNode
  testId: string
}) {
  return (
    <section className="mb-4 last:mb-0" data-testid={testId}>
      <h3 className="text-foreground/80 mb-1.5 text-xs font-semibold tracking-wide uppercase">
        {heading}
      </h3>
      {children}
    </section>
  )
}

export function PlanRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  citationSection,
  sessionType,
}: PlanRendererProps) {
  const { t } = useTranslation()
  const payload = artifact.payload
  // `understanding` and `expected_result` are required by the type but the
  // contract allows them to be empty strings when the planner couldn't
  // populate them — the parsing helper falls back to "" rather than dropping
  // the artifact, so we still need to trim defensively.
  const understanding = payload.understanding.trim()
  const expected = payload.expected_result.trim()
  const sources = payload.sources_to_consult ?? []
  const todos = payload.todos ?? []
  const stepsMarkdown = (payload.steps_markdown ?? "").trim()
  const format = payload.deliverable_format ?? null
  const missionId = artifact.trace_ref.mission_id || null

  const framedArtifact = useMemo<Artifact>(() => {
    // Default action set: when the artifact didn't ship any actions, add a
    // view_trace affordance if there's a mission to point at. The plan is a
    // read-only summary — there's no copy/download default because the
    // approval flow lives elsewhere (legacy in-chat bar).
    const hasActions = (artifact.actions ?? []).length > 0
    if (hasActions) return artifact
    const defaults: ArtifactAction[] = []
    if (missionId) {
      defaults.push({
        id: "view_trace",
        label: t("deepExplorer.artifact_frame.view_trace", { defaultValue: "View trace" }),
        icon: "zap",
      })
    }
    return { ...artifact, actions: defaults }
  }, [artifact, missionId, t])

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      className="plan-card"
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="space-y-1 text-sm" data-testid="plan-renderer-body">
        {format ? (
          <div className="mb-3" data-testid="plan-format-chip">
            <span className="border-border bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium">
              {t("deepExplorer.plan_workspace.format_label", { defaultValue: "Format" })}: {format}
            </span>
          </div>
        ) : null}

        {understanding ? (
          <PlanSection
            testId="plan-section-understanding"
            heading={t("deepExplorer.plan_understanding", {
              defaultValue: "How I understand the question",
            })}
          >
            <WorkspaceMarkdownRenderer citationSection={citationSection} content={understanding} />
          </PlanSection>
        ) : null}

        {expected ? (
          <PlanSection
            testId="plan-section-expected"
            heading={t("deepExplorer.plan_expected_result", {
              defaultValue: "Expected result",
            })}
          >
            <WorkspaceMarkdownRenderer citationSection={citationSection} content={expected} />
          </PlanSection>
        ) : null}

        {sources.length > 0 ? (
          <PlanSection
            testId="plan-section-sources"
            heading={t("deepExplorer.plan_sources", {
              defaultValue: "Sources I'll consult",
            })}
          >
            <ul className="list-inside list-disc space-y-0.5 pl-1">
              {sources.map((src, i) => (
                <li
                  key={`${i}-${src}`}
                  className="text-foreground/90"
                  data-testid="plan-source-item"
                >
                  {src}
                </li>
              ))}
            </ul>
          </PlanSection>
        ) : null}

        {todos.length > 0 ? (
          <PlanSection
            heading={t("deepExplorer.plan_todos", { defaultValue: "Todos" })}
            testId="plan-section-todos"
          >
            <ul className="space-y-1.5">
              {todos.map((todo) => (
                <li
                  key={todo.id}
                  className="flex items-start gap-2"
                  data-status={todo.status}
                  data-testid="plan-todo-item"
                >
                  <StatusIcon status={todo.status} />
                  <span className="flex-1 leading-snug">{todo.text}</span>
                  <span
                    data-testid="plan-todo-status-badge"
                    className={cn(
                      "shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                      STATUS_BADGE_STYLES[todo.status]
                    )}
                  >
                    {t(STATUS_LABEL[todo.status].key, {
                      defaultValue: STATUS_LABEL[todo.status].fallback,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </PlanSection>
        ) : null}

        {stepsMarkdown ? (
          <PlanSection
            heading={t("deepExplorer.plan_workspace.steps_heading", { defaultValue: "Steps" })}
            testId="plan-section-steps"
          >
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
              <WorkspaceMarkdownRenderer
                citationSection={citationSection}
                content={stepsMarkdown}
              />
            </div>
          </PlanSection>
        ) : null}

        {/* Empty-state guard: the contract says the renderer must handle a
            payload with no fields populated. Show a tiny hint rather than a
            blank card so the user understands the artifact is intentional. */}
        {!understanding &&
        !expected &&
        sources.length === 0 &&
        todos.length === 0 &&
        !stepsMarkdown ? (
          <p className="text-muted-foreground py-2 text-xs italic" data-testid="plan-empty-state">
            {t("deepExplorer.plan_workspace.empty_state", {
              defaultValue: "The plan is empty. The agent will fill it in shortly.",
            })}
          </p>
        ) : null}

        {/* Phase 1 (workspace-focus): inline approval controls. Only renders
            when sessionType is wired AND the store says the plan is awaiting
            decision. Legacy / test rendering without sessionType keeps the
            renderer read-only as before. */}
        {sessionType ? (
          <PlanApprovalControls artifact={artifact} sessionType={sessionType} onAction={onAction} />
        ) : null}
      </div>
    </ArtifactFrame>
  )
}
